import type { DivisionBar, DivisionId, DoorConfig } from '@/lib/configurator/options'

import {
    findDoorType,
    findProfile,
    sidePanelCount,
    hasTransom
} from '@/lib/configurator/options'

export interface Rect {
    x: number
    y: number
    w: number
    h: number
}

export interface Bar extends Rect {
    orientation: 'h' | 'v'
}

export interface Segment {
    orientation: 'h' | 'v'
    at: number
    from: number
    to: number
}

export interface HandlePlacement {
    x: number
    y: number
    length: number
    edge: 'left' | 'right'
}

export interface LeafLayout {
    key: string
    rect: Rect
    members: Rect[]
    glass: Rect
    bars: Bar[]
    bottomPanel: Rect | null
    hinge: 'left' | 'right'
    pivotX: number | null
    mirrored: boolean
    handle: HandlePlacement
}

export interface PanelLayout {
    key: string
    kind: 'side' | 'transom'
    glass: Rect
    bars: Bar[]
}

export interface DoorMetrics {
    glassArea: number
    barLength: number
    doorArea: number
    totalArea: number
    sidePanelAreas: number[]
    transomArea: number
}

export interface DoorLayout {
    sliding: boolean
    slideDirection: 'left' | 'right'
    width: number
    height: number
    frameWidth: number
    leafProfile: number
    barWidth: number
    frame: Rect[]
    leaves: LeafLayout[]
    panels: PanelLayout[]
    rail: Rect | null
    bounds: Rect
    metrics: DoorMetrics
}

export interface LayoutOverrides {
    leftSide?: number | null
    rightSide?: number | null
}

const FLOOR_GAP = 10
const GAP = 3
const SLIDE_OVERLAP = 50

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

const range = (count: number): number[] => Array.from({ length: Math.max(0, count) }, (_, index) => index)

export const getDivisionSegments = (division: DivisionId, rows: number, cols: number): Segment[] => {
    const horizontal = (count: number): Segment[] => range(count).map(index => ({ orientation: 'h', at: (index + 1) / (count + 1), from: 0, to: 1 }))
    const vertical = (count: number): Segment[] => range(count).map(index => ({ orientation: 'v', at: (index + 1) / (count + 1), from: 0, to: 1 }))

    switch (division) {
        case 'horizontaal':
            return horizontal(rows)
        case 'verticaal':
            return vertical(cols)
        case 'raster':
            return [...horizontal(rows), ...vertical(cols)]
        case 'herenhuis':
            return [
                { orientation: 'h', at: 0.36, from: 0, to: 1 },
                { orientation: 'h', at: 0.78, from: 0, to: 1 },
                { orientation: 'v', at: 1 / 3, from: 0.78, to: 1 },
                { orientation: 'v', at: 2 / 3, from: 0.78, to: 1 }
            ]
        case 'bauhaus':
            return [
                { orientation: 'v', at: 0.2, from: 0, to: 1 },
                { orientation: 'v', at: 0.8, from: 0, to: 1 },
                ...[0.25, 0.5, 0.75].flatMap<Segment>(at => [
                    { orientation: 'h', at, from: 0, to: 0.2 },
                    { orientation: 'h', at, from: 0.8, to: 1 }
                ])
            ]
        case 'artdeco':
            return [
                { orientation: 'h', at: 0.18, from: 0, to: 1 },
                { orientation: 'h', at: 0.82, from: 0, to: 1 },
                { orientation: 'v', at: 1 / 3, from: 0.82, to: 1 },
                { orientation: 'v', at: 2 / 3, from: 0.82, to: 1 },
                { orientation: 'v', at: 1 / 3, from: 0, to: 0.18 },
                { orientation: 'v', at: 2 / 3, from: 0, to: 0.18 }
            ]
        default:
            return []
    }
}

const segmentsToBars = (segments: Segment[], glass: Rect, barWidth: number): Bar[] => {
    return segments.map(segment => {
        if (segment.orientation === 'h') {
            const cy = glass.y + segment.at * glass.h
            const x1 = glass.x + segment.from * glass.w
            const x2 = glass.x + segment.to * glass.w

            return { orientation: 'h', x: x1, y: cy - barWidth / 2, w: x2 - x1, h: barWidth }
        }

        const cx = glass.x + segment.at * glass.w
        const y1 = glass.y + segment.from * glass.h
        const y2 = glass.y + segment.to * glass.h

        return { orientation: 'v', x: cx - barWidth / 2, y: y1, w: barWidth, h: y2 - y1 }
    })
}

const MARGIN = 25

const millimeterBars = (bars: DivisionBar[], rect: Rect, glass: Rect, barWidth: number, mirrored: boolean): Bar[] => {
    const leafX = (value: number) => (mirrored ? rect.x + rect.w - value : rect.x + value)
    const result: Bar[] = []

    for (const bar of bars) {
        if (bar.orientation === 'h') {
            const cy = bar.at

            if (cy - barWidth / 2 < glass.y + MARGIN || cy + barWidth / 2 > glass.y + glass.h - MARGIN) continue

            const a = bar.from === null ? glass.x : clamp(leafX(bar.from), glass.x, glass.x + glass.w)
            const b = bar.to === null ? glass.x + glass.w : clamp(leafX(bar.to), glass.x, glass.x + glass.w)
            const x1 = Math.min(a, b)
            const x2 = Math.max(a, b)

            if (x2 - x1 < 10) continue

            result.push({ orientation: 'h', x: x1, y: cy - barWidth / 2, w: x2 - x1, h: barWidth })
        }
        else {
            const cx = leafX(bar.at)

            if (cx - barWidth / 2 < glass.x + MARGIN || cx + barWidth / 2 > glass.x + glass.w - MARGIN) continue

            const a = bar.from === null ? glass.y : clamp(bar.from, glass.y, glass.y + glass.h)
            const b = bar.to === null ? glass.y + glass.h : clamp(bar.to, glass.y, glass.y + glass.h)
            const y1 = Math.min(a, b)
            const y2 = Math.max(a, b)

            if (y2 - y1 < 10) continue

            result.push({ orientation: 'v', x: cx - barWidth / 2, y: y1, w: barWidth, h: y2 - y1 })
        }
    }

    return result
}

const handleLength = (config: DoorConfig, leafHeight: number): number => {
    switch (config.handle) {
        case 'kruk':
        case 'vaste-kruk':
            return 140
        case 'klein':
        case 'op-aanvraag':
            return 100
        case 'lang':
            return Math.max(600, Math.round(leafHeight - 260))
        default:
            return 100
    }
}

const handleCenterY = (config: DoorConfig, rect: Rect, length: number): number => {
    if (config.handle === 'lang') return rect.y + rect.h / 2

    const min = rect.y + length / 2 + 120
    const max = rect.y + rect.h - length / 2 - 120

    return clamp(1050, min, Math.max(min, max))
}

const buildLeaf = (config: DoorConfig, key: string, rect: Rect, hinge: 'left' | 'right', pivot: boolean, mirrored: boolean, profile: number, barWidth: number): LeafLayout => {
    const P = profile

    const members: Rect[] = [
        { x: rect.x, y: rect.y, w: P, h: rect.h },
        { x: rect.x + rect.w - P, y: rect.y, w: P, h: rect.h },
        { x: rect.x + P, y: rect.y + rect.h - P, w: rect.w - 2 * P, h: P },
        { x: rect.x + P, y: rect.y, w: rect.w - 2 * P, h: P }
    ]

    let glassY = rect.y + P
    let bottomPanel: Rect | null = null

    if (config.bottomPanel) {
        const panelHeight = clamp(Math.round(rect.h * 0.17), 300, 450)

        bottomPanel = { x: rect.x + P, y: rect.y + P, w: rect.w - 2 * P, h: panelHeight }
        members.push({ x: rect.x + P, y: rect.y + P + panelHeight, w: rect.w - 2 * P, h: P })
        glassY = rect.y + P + panelHeight + P
    }

    const glass: Rect = { x: rect.x + P, y: glassY, w: rect.w - 2 * P, h: rect.y + rect.h - P - glassY }

    const bars = config.division === 'eigen'
        ? millimeterBars(config.bars, rect, glass, barWidth, mirrored)
        : segmentsToBars(getDivisionSegments(config.division, config.rows, config.cols), glass, barWidth)

    const edge: 'left' | 'right' = hinge === 'left' ? 'right' : 'left'
    const length = handleLength(config, rect.h)
    const handleX = edge === 'right' ? rect.x + rect.w - P / 2 : rect.x + P / 2

    const pivotOffset = clamp(Math.round(rect.w * 0.2), 150, 280)
    const pivotX = pivot ? (hinge === 'left' ? rect.x + pivotOffset : rect.x + rect.w - pivotOffset) : null

    return {
        key,
        rect,
        members,
        glass,
        bars,
        bottomPanel,
        hinge,
        pivotX,
        mirrored,
        handle: {
            x: handleX,
            y: handleCenterY(config, rect, length),
            length,
            edge
        }
    }
}

const area = (rect: Rect): number => Math.max(0, rect.w) * Math.max(0, rect.h)

const barLength = (bars: Bar[]): number => bars.reduce((sum, bar) => sum + (bar.orientation === 'h' ? bar.w : bar.h), 0)

const computeSlidingLayout = (config: DoorConfig): DoorLayout => {
    const profile = findProfile(config.profile)
    const W = config.width
    const H = config.height
    const slideDirection: 'left' | 'right' = config.hinge === 'links' ? 'left' : 'right'

    const leafRect: Rect = { x: -SLIDE_OVERLAP, y: FLOOR_GAP, w: W + 2 * SLIDE_OVERLAP, h: H + 30 - FLOOR_GAP }
    const leaf = buildLeaf(config, 'schuifdeur', leafRect, slideDirection, false, false, profile.leaf, profile.bar)

    const railY = leafRect.y + leafRect.h + 15
    const railLength = 2 * W + 250

    const rail: Rect = slideDirection === 'left'
        ? { x: -(W + 175), y: railY, w: railLength, h: 40 }
        : { x: -75, y: railY, w: railLength, h: 40 }

    return {
        sliding: true,
        slideDirection,
        width: W,
        height: H,
        frameWidth: 0,
        leafProfile: profile.leaf,
        barWidth: profile.bar,
        frame: [],
        leaves: [leaf],
        panels: [],
        rail,
        bounds: { x: rail.x, y: 0, w: rail.w, h: rail.y + rail.h },
        metrics: {
            glassArea: area(leaf.glass) / 1e6,
            barLength: barLength(leaf.bars) / 1000,
            doorArea: (W * H) / 1e6,
            totalArea: (leafRect.w * (leafRect.h + FLOOR_GAP)) / 1e6,
            sidePanelAreas: [],
            transomArea: 0
        }
    }
}

export const computeLayout = (config: DoorConfig, overrides: LayoutOverrides = {}): DoorLayout => {
    if (config.doorType === 'schuifdeur') return computeSlidingLayout(config)

    const type = findDoorType(config.doorType)
    const profile = findProfile(config.profile)

    const F = profile.frame
    const B = profile.bar

    const sides = sidePanelCount(config)
    const hasLeft = sides > 0 && (config.sidePanels === 'links' || config.sidePanels === 'beide')
    const hasRight = sides > 0 && (config.sidePanels === 'rechts' || config.sidePanels === 'beide')
    const leftSide = hasLeft ? (overrides.leftSide || config.sidePanelWidth) : 0
    const rightSide = hasRight ? (overrides.rightSide || config.sidePanelWidth) : 0
    const transom = hasTransom(config)

    const W = leftSide + config.width + rightSide
    const doorH = config.height
    const H = doorH + (transom ? config.transomHeight : 0)

    const frame: Rect[] = [
        { x: 0, y: 0, w: F, h: H },
        { x: W - F, y: 0, w: F, h: H },
        { x: F, y: H - F, w: W - 2 * F, h: F }
    ]

    if (transom) frame.push({ x: F, y: doorH - F, w: W - 2 * F, h: F })

    const boundaries: number[] = []

    if (leftSide) boundaries.push(leftSide)
    if (rightSide) boundaries.push(leftSide + config.width)

    for (const boundary of boundaries) {
        frame.push({ x: boundary - F / 2, y: 0, w: F, h: doorH - F })

        if (transom) frame.push({ x: boundary - F / 2, y: doorH, w: F, h: H - F - doorH })
    }

    const openX0 = leftSide ? leftSide + F / 2 : F
    const openX1 = rightSide ? leftSide + config.width - F / 2 : W - F
    const openTop = doorH - F
    const leafHeight = openTop - GAP - FLOOR_GAP

    const hinge: 'left' | 'right' = config.hinge === 'links' ? 'left' : 'right'
    const pivot = config.doorType === 'taatsdeur'

    const leaves: LeafLayout[] = []

    if (type.leaves === 2) {
        const leafWidth = (openX1 - openX0 - 3 * GAP) / 2

        leaves.push(buildLeaf(config, 'links', { x: openX0 + GAP, y: FLOOR_GAP, w: leafWidth, h: leafHeight }, 'left', false, false, profile.leaf, B))
        leaves.push(buildLeaf(config, 'rechts', { x: openX0 + 2 * GAP + leafWidth, y: FLOOR_GAP, w: leafWidth, h: leafHeight }, 'right', false, true, profile.leaf, B))
    }
    else {
        leaves.push(buildLeaf(config, 'deur', { x: openX0 + GAP, y: FLOOR_GAP, w: openX1 - openX0 - 2 * GAP, h: leafHeight }, hinge, pivot, false, profile.leaf, B))
    }

    const panels: PanelLayout[] = []
    const firstLeaf = leaves[0]
    const barCenters = firstLeaf.bars
        .filter(bar => bar.orientation === 'h' && Math.abs(bar.w - firstLeaf.glass.w) < 1)
        .map(bar => bar.y + bar.h / 2)

    const sidePanel = (key: string, glass: Rect): PanelLayout => ({
        key,
        kind: 'side',
        glass,
        bars: barCenters
            .filter(center => center - B / 2 > glass.y + 40 && center + B / 2 < glass.y + glass.h - 40)
            .map<Bar>(center => ({ orientation: 'h', x: glass.x, y: center - B / 2, w: glass.w, h: B }))
    })

    if (leftSide) {
        const width = leftSide - F / 2 - F

        frame.push({ x: F, y: 0, w: width, h: F })
        panels.push(sidePanel('zijpaneel-links', { x: F, y: F, w: width, h: doorH - 2 * F }))
    }

    if (rightSide) {
        const x = leftSide + config.width + F / 2
        const width = W - F - x

        frame.push({ x, y: 0, w: width, h: F })
        panels.push(sidePanel('zijpaneel-rechts', { x, y: F, w: width, h: doorH - 2 * F }))
    }

    if (transom) {
        const edges = [F, ...boundaries.flatMap(boundary => [boundary - F / 2, boundary + F / 2]), W - F]

        for (let index = 0; index < edges.length; index += 2) {
            panels.push({
                key: `bovenlicht-${index / 2}`,
                kind: 'transom',
                glass: { x: edges[index], y: doorH, w: edges[index + 1] - edges[index], h: H - F - doorH },
                bars: []
            })
        }
    }

    const allBars = [...leaves.flatMap(leaf => leaf.bars), ...panels.flatMap(panel => panel.bars)]
    const allGlass = [...leaves.map(leaf => leaf.glass), ...panels.map(panel => panel.glass)]

    return {
        sliding: false,
        slideDirection: 'left',
        width: W,
        height: H,
        frameWidth: F,
        leafProfile: profile.leaf,
        barWidth: B,
        frame,
        leaves,
        panels,
        rail: null,
        bounds: { x: 0, y: 0, w: W, h: H },
        metrics: {
            glassArea: allGlass.reduce((sum, rect) => sum + area(rect), 0) / 1e6,
            barLength: barLength(allBars) / 1000,
            doorArea: (config.width * doorH) / 1e6,
            totalArea: (W * H) / 1e6,
            sidePanelAreas: [leftSide, rightSide].filter(width => width > 0).map(width => (width * doorH) / 1e6),
            transomArea: transom ? (W * config.transomHeight) / 1e6 : 0
        }
    }
}

export const overallDimensions = (config: DoorConfig): { width: number, height: number } => {
    if (config.doorType === 'schuifdeur') return { width: config.width, height: config.height }

    const sides = sidePanelCount(config) * config.sidePanelWidth

    return {
        width: config.width + sides,
        height: config.height + (hasTransom(config) ? config.transomHeight : 0)
    }
}

export interface BarLimits {
    hMin: number
    hMax: number
    vMin: number
    vMax: number
    glass: Rect
    rect: Rect
}

export const barLimits = (config: DoorConfig): BarLimits => {
    const layout = computeLayout({ ...config, division: 'strak', bars: [] })
    const leaf = layout.leaves[0]
    const B = layout.barWidth

    return {
        hMin: Math.ceil(leaf.glass.y + MARGIN + B / 2),
        hMax: Math.floor(leaf.glass.y + leaf.glass.h - MARGIN - B / 2),
        vMin: Math.ceil(leaf.glass.x - leaf.rect.x + MARGIN + B / 2),
        vMax: Math.floor(leaf.glass.x - leaf.rect.x + leaf.glass.w - MARGIN - B / 2),
        glass: leaf.glass,
        rect: leaf.rect
    }
}

export const presetBarsMm = (config: DoorConfig): DivisionBar[] => {
    if (config.division === 'eigen') return config.bars

    const { glass, rect } = barLimits(config)

    return getDivisionSegments(config.division, config.rows, config.cols).map<DivisionBar>(segment => {
        const full = segment.from <= 0 && segment.to >= 1

        if (segment.orientation === 'h') {
            return {
                orientation: 'h',
                at: Math.round(glass.y + segment.at * glass.h),
                from: full ? null : Math.round(glass.x + segment.from * glass.w - rect.x),
                to: full ? null : Math.round(glass.x + segment.to * glass.w - rect.x)
            }
        }

        return {
            orientation: 'v',
            at: Math.round(glass.x + segment.at * glass.w - rect.x),
            from: full ? null : Math.round(glass.y + segment.from * glass.h),
            to: full ? null : Math.round(glass.y + segment.to * glass.h)
        }
    })
}

export const effectiveBars = (config: DoorConfig): DivisionBar[] => {
    const bars = config.division === 'eigen' ? config.bars : presetBarsMm(config)

    return [...bars].sort((a, b) => (a.orientation === b.orientation ? a.at - b.at : a.orientation === 'h' ? -1 : 1))
}

export const rescaleBars = (previous: DoorConfig, next: DoorConfig): DivisionBar[] => {
    const before = barLimits(previous)
    const after = barLimits(next)

    const scaleY = (value: number) => Math.round(after.glass.y + ((value - before.glass.y) / Math.max(1, before.glass.h)) * after.glass.h)
    const offsetBefore = before.glass.x - before.rect.x
    const offsetAfter = after.glass.x - after.rect.x
    const scaleX = (value: number) => Math.round(offsetAfter + ((value - offsetBefore) / Math.max(1, before.glass.w)) * after.glass.w)

    return previous.bars.map(bar => bar.orientation === 'h'
        ? { ...bar, at: scaleY(bar.at), from: bar.from === null ? null : scaleX(bar.from), to: bar.to === null ? null : scaleX(bar.to) }
        : { ...bar, at: scaleX(bar.at), from: bar.from === null ? null : scaleY(bar.from), to: bar.to === null ? null : scaleY(bar.to) })
}

export const distributeBars = (config: DoorConfig, bars: DivisionBar[], orientation: 'h' | 'v'): DivisionBar[] => {
    const limits = barLimits(config)
    const full = bars.filter(bar => bar.orientation === orientation && bar.from === null && bar.to === null)
    const others = bars.filter(bar => !full.includes(bar))
    const count = full.length

    if (!count) return bars

    const start = orientation === 'h' ? limits.glass.y : limits.glass.x - limits.rect.x
    const size = orientation === 'h' ? limits.glass.h : limits.glass.w
    const spread = range(count).map<DivisionBar>(index => ({ orientation, at: Math.round(start + ((index + 1) / (count + 1)) * size), from: null, to: null }))

    return [...others, ...spread]
}
